import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, ChartBarSquareIcon, PresentationChartLineIcon, TableCellsIcon } from '@heroicons/react/20/solid';
import ComingSoon, { ComingSoonPoint } from '@app/component/placeholder/ComingSoon';
import FormButton from '@app/component/form/button/FormButton';
import KpiStrip from '@app/component/chart/KpiStrip';
import ChartCard from '@app/component/chart/ChartCard';
import Heatmap from '@app/component/chart/Heatmap';
import Message from '@app/component/message/Message';
import DashboardPlaceholder from '@app/component/placeholder/DashboardPlaceholder';
import BiPageHeader from '../fragment/BiPageHeader';
import { MONTH_LABELS, propertyCount, shortName } from '../Bi.utilities';
import { useAccountBiScope, useBiAddress } from '../BiScopeHooks';

const POINTS: ComingSoonPoint[] = [
	{ icon: PresentationChartLineIcon, title: 'The year at a glance', text: 'Room revenue, occupancy, ADR, RevPAR and room nights, for all properties or one hotel.' },
	{ icon: TableCellsIcon, title: 'Every hotel, every month', text: 'A heatmap with one row per property and one cell per month.' },
	{ icon: ChartBarSquareIcon, title: 'From your reservations', text: 'The numbers come from the imported PMS reservations and the room counts in Settings.' },
];
const PREVIEW_MONTHS = MONTH_LABELS.slice(0, 8);

// The Dashboard of the BI section comes in the next ticket: its occupancy and RevPAR need the room count (Settings
// RQ-28) and the opening season (RQ-25). Until then the page says what it will show, with a faded preview made of the
// real Components with empty values (no number), and a button to the Segmentation report, which is ready.
const BiDashboardPage = () => {
	const scope = useAccountBiScope();
	const address = useBiAddress(scope.data);
	const navigate = useNavigate();

	if (scope.isError) return <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
		<Message state="error" title="The BI section did not load" flush>Reload the page. If the problem stays, contact RoomPulse.</Message>
	</main>;
	if (scope.data === undefined) return <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8"><DashboardPlaceholder /></main>;

	const s = scope.data;
	const preview = <div className="flex flex-col gap-4">
		<KpiStrip ariaLabel="Preview of the Dashboard" items={[
			{ id: 'revenue', label: 'Room revenue', value: null, format: 'money' },
			{ id: 'occupancy', label: 'Occupancy', value: null, format: 'percent' },
			{ id: 'adr', label: 'ADR', value: null, format: 'money' },
			{ id: 'revpar', label: 'RevPAR', value: null, format: 'money' },
		]} />
		<ChartCard title="Occupancy by hotel and month" period="One row per property, one cell per month">
			<Heatmap columns={PREVIEW_MONTHS} format="percent" closedLabel="" ariaLabel="Preview of the heatmap" rowHeader="Property"
				rows={s.properties.map(p => ({ id: String(p.companyHotelId), label: shortName(p, s.companyName), values: PREVIEW_MONTHS.map(() => null) }))} />
		</ChartCard>
	</div>;

	return <>
		<BiPageHeader scope={s} companyHotelId={null} search={address.search} selectable={false} title="Dashboard" page="Dashboard"
			sublabel={`${s.companyName} · ${propertyCount(s.properties.length)}`} />
		<main className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8">
			<ComingSoon title="The BI Dashboard is on its way" preview={preview} points={POINTS}
				description="One page with the headline numbers of your properties. Until then, the Segmentation report shows your room nights and revenue by your own groups of PMS segments."
				actions={<>
					<FormButton state="primary" icon={<ArrowRightIcon aria-hidden="true" className="h-4 w-4" />}
						onClick={() => navigate(`/secured/bi/segmentation${address.search === '' ? '' : `?${address.search}`}`)}>
						Open Segmentation
					</FormButton>
				</>} />
		</main>
	</>;
};

export default BiDashboardPage;
